import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme, apiClient } from "@/App";
import { resolveMediaUrl } from "@/lib/media-url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Check, 
  Camera,
  Phone,
  CalendarBlank,
  MapPin,
  Books,
  Student,
  User,
  Envelope,
  PencilSimple
} from "@phosphor-icons/react";

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [skillsInput, setSkillsInput] = useState(user?.skills?.join(", ") || "");
  const [mobileNo, setMobileNo] = useState(user?.mobile_no || "");
  const [dateOfBirth, setDateOfBirth] = useState(user?.date_of_birth || "");
  const [homeAddress, setHomeAddress] = useState(user?.home_address || "");
  const [batchName, setBatchName] = useState(user?.batch_name || "");
  const [courseName, setCourseName] = useState(user?.course_name || "");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
  
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, PNG, GIF, WEBP allowed.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB.");
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post("/profile/avatar", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setAvatarUrl(response.data.avatar_url);
      updateUser({ ...user, avatar_url: response.data.avatar_url });
      toast.success("Profile picture updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    try {
      const skills = skillsInput
        .split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const response = await apiClient.put("/profile", {
        full_name: fullName,
        bio: bio || null,
        skills,
        mobile_no: mobileNo || null,
        date_of_birth: dateOfBirth || null,
        home_address: homeAddress || null,
        batch_name: batchName || null,
        course_name: courseName || null,
      });
      
      updateUser(response.data);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const getAvatarSrc = () => {
    if (avatarUrl && avatarUrl.startsWith("/api/uploads/")) {
      return resolveMediaUrl(avatarUrl);
    }
    return avatarUrl;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-surface transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold">Edit Profile</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSave} className="space-y-6">
          {/* Profile Picture Section */}
          <div className="bg-surface rounded-xl p-6 border border-border/40">
            <h2 className="font-heading font-semibold text-lg mb-4">Profile Picture</h2>
            
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Avatar Preview */}
              <div className="relative">
                {avatarUrl ? (
                  <img
                    src={getAvatarSrc()}
                    alt={fullName}
                    className="w-24 h-24 rounded-full object-cover border-2 border-primary/30"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-4xl font-bold text-background">
                    {fullName?.charAt(0).toUpperCase() || "?"}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="change-avatar-button"
                >
                  {uploadingAvatar ? (
                    <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="avatar-file-input"
                />
              </div>

              <div className="flex-1 text-center sm:text-left">
                <p className="font-medium">{user?.email}</p>
                <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Click the camera icon to upload a new photo. Max 5MB, JPEG/PNG/GIF/WEBP.
                </p>
              </div>
            </div>
          </div>

          {/* Basic Info Section */}
          <div className="bg-surface rounded-xl p-6 border border-border/40 space-y-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Basic Information
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-background border-border focus:border-primary"
                  data-testid="settings-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Envelope className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-background/50 border-border text-muted-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                rows={3}
                className="bg-background border-border focus:border-primary resize-none"
                data-testid="settings-bio-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="skills">Skills (comma separated)</Label>
              <Input
                id="skills"
                value={skillsInput}
                onChange={(e) => setSkillsInput(e.target.value)}
                placeholder="React, Python, MongoDB, CSS..."
                className="bg-background border-border focus:border-primary"
                data-testid="settings-skills-input"
              />
              {skillsInput && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {skillsInput.split(",").map((skill, idx) => {
                    const trimmed = skill.trim();
                    if (!trimmed) return null;
                    return (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs"
                      >
                        {trimmed}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Contact & Personal Section */}
          <div className="bg-surface rounded-xl p-6 border border-border/40 space-y-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Contact & Personal Details
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mobileNo" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Mobile Number
                </Label>
                <Input
                  id="mobileNo"
                  type="tel"
                  value={mobileNo}
                  onChange={(e) => setMobileNo(e.target.value)}
                  placeholder="+91 9876543210"
                  className="bg-background border-border focus:border-primary"
                  data-testid="settings-mobile-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateOfBirth" className="flex items-center gap-2">
                  <CalendarBlank className="w-4 h-4" />
                  Date of Birth
                </Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="bg-background border-border focus:border-primary"
                  data-testid="settings-dob-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="homeAddress" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Home Address
              </Label>
              <Textarea
                id="homeAddress"
                value={homeAddress}
                onChange={(e) => setHomeAddress(e.target.value)}
                placeholder="Enter your complete address"
                rows={2}
                className="bg-background border-border focus:border-primary resize-none"
                data-testid="settings-address-input"
              />
            </div>
          </div>

          {/* Academic Info Section */}
          <div className="bg-surface rounded-xl p-6 border border-border/40 space-y-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <Books className="w-5 h-5 text-primary" />
              Academic Information
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="batchName" className="flex items-center gap-2">
                  <Student className="w-4 h-4" />
                  Batch Name
                </Label>
                <Input
                  id="batchName"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  placeholder="e.g., 2024-A, Morning Batch"
                  className="bg-background border-border focus:border-primary"
                  data-testid="settings-batch-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="courseName" className="flex items-center gap-2">
                  <Books className="w-4 h-4" />
                  Course Name
                </Label>
                <Input
                  id="courseName"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="e.g., Web Development, Python"
                  className="bg-background border-border focus:border-primary"
                  data-testid="settings-course-input"
                />
              </div>
            </div>
          </div>

          {/* Appearance Section */}
          <div className="bg-surface rounded-xl p-6 border border-border/40 space-y-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <PencilSimple className="w-5 h-5 text-primary" />
              Appearance
            </h2>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  theme === "dark"
                    ? "bg-primary text-primary-foreground"
                    : "bg-background border border-border text-muted-foreground"
                }`}
                data-testid="theme-dark-button"
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  theme === "light"
                    ? "bg-primary text-primary-foreground"
                    : "bg-background border border-border text-muted-foreground"
                }`}
                data-testid="theme-light-button"
              >
                Light
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Theme preference is saved on this device.</p>
          </div>

          {/* Save Button */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold"
            data-testid="save-settings-button"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                Save Changes
              </span>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
